import { Box, Stack } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { editProjectIndent, Indent, qk } from 'api'
import { DialogContent, ToastContent } from 'elements/feedback'
import { Select } from 'elements/inputs'
import { getInputError } from 'lib/form-utils/get-input-error'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

const formId = 'EDIT_INDENTS_FORM'

const indentValues = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

type FormValues = {
  json: number
  xml: number
}

type EditProjectIndentFormProps = {
  projectId: string
  indent: Indent
  onClose: () => void
}

export const EditProjectIndentForm = ({
  projectId,
  indent,
  onClose,
}: EditProjectIndentFormProps) => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const $editProjectIndent = useMutation(editProjectIndent)

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      json: indent.json,
      xml: indent.xml,
    },
  })

  return (
    <DialogContent
      title={t('projects.settings.indents.change_indents')}
      isActionLoading={$editProjectIndent.isLoading}
      externalFormId={formId}
      onDeny={onClose}
      denyText={t('common.cancel')}
      isConfirmDisabled={!isDirty}
    >
      <Box
        id={formId}
        component="form"
        onSubmit={handleSubmit(({ json, xml }) =>
          $editProjectIndent.mutate(
            {
              projectId,
              json,
              xml,
            },
            {
              onSuccess: () => {
                onClose()
                queryClient.invalidateQueries(
                  qk.projects.details.toKeyWithArgs({ projectId }),
                )
                toast.success(
                  <ToastContent
                    title={t(
                      'projects.settings.indents.indents_successfully_changed',
                    )}
                  />,
                )
              },
            },
          ),
        )}
      >
        <Stack direction="row" spacing={3}>
          <Controller
            name="json"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                label={t('projects.settings.indents.json_indent')}
                options={indentValues.map(number => ({
                  value: number,
                  label: number,
                }))}
                {...getInputError(fieldState.error)}
                {...field}
              />
            )}
          />

          <Controller
            name="xml"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                label={t('projects.settings.indents.xml_indent')}
                options={indentValues.map(number => ({
                  value: number,
                  label: number,
                }))}
                {...getInputError(fieldState.error)}
                {...field}
              />
            )}
          />
        </Stack>
      </Box>
    </DialogContent>
  )
}
